'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Icon, Modal, NumberInput, Tooltip } from '~/components/primitives';
import React, { startTransition, useEffect, useMemo, useState } from 'react';
import { useShouldDisplayPrices, useVATRate, VATKindDisplay } from '~/features/portals/money';
import { AnimatePresence } from 'motion/react';
import { I18n_AmountPerParticipant } from '~/features/i18n/translations';
import { Units } from '~/modules/units';
import { cx } from 'class-variance-authority';
import { useI18n } from '~/features/i18n';
import { useStateObject } from '~/utils/react';
import Title from '~/components/utils/Title';
import QuotationCard from './QuotationCard';
import { Filters, maybeMap, std } from '@funkey/shared';
import z from 'zod';
import type { ActivityFields } from './shared';
import { calculatePrice, variantPriceSpan } from '@funkey/shared/activities';
import { usePortalCtx } from '~/features/global/portal';
import { ActivityPDP_useCtx } from './context';
import { useStaffLayer_State } from './staff-layer/state';
import { shouldShowVariantPriceRules } from './variant-price-rules';
export const ActivityPageVariants: React.FC<{
  allowBooking: boolean;
}> = props => {
  const $ = _c(14);
  const ctx = ActivityPDP_useCtx();
  const modalVariantId = useStateObject(null);
  const shouldDisplayPrices = useShouldDisplayPrices();
  if (!ctx.activity.variants.length) {
    return null;
  }
  const t0 = shouldDisplayPrices && <ActivityPDP_Variants_ParticipantsInput key={String(ctx.participants.isMounted)} participants={{
    state: ctx.participants.state,
    setState: ctx.participants.setState,
    min: ctx.participants.min ?? null,
    max: ctx.participants.max ?? null
  }} activity={{
    priceType: ctx.activity.priceType,
    variants: ctx.activity.variants
  }} />;
  const t1 = "flex flex-col gap-y-3";
  let t2;
  if ($[0] !== modalVariantId || $[1] !== props.allowBooking) {
    t2 = variant => <ActivityVariantCard key={variant.id} variant={variant} onClick={props.allowBooking ? () => modalVariantId.set(variant.id) : undefined} />;
    $[0] = modalVariantId;
    $[1] = props.allowBooking;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = ctx.activity.variants.map(t2);
  let t4;
  if ($[3] !== t3) {
    t4 = <div className={t1}>{t3}</div>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== ctx || $[6] !== modalVariantId) {
    t5 = modalVariantId.value && <Modal onClose={() => modalVariantId.set(null)} className="w-full sm:max-w-lg"><Modal.Content><QuotationCard activity={{
          id: ctx.activity.id,
          activityLanguages: ctx.activity.languages,
          activityVariants: ctx.activity.variants
        }} variantId={modalVariantId.value} onSubmit={() => modalVariantId.set(null)} /></Modal.Content></Modal>;
    $[5] = ctx;
    $[6] = modalVariantId;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <AnimatePresence>{t5}</AnimatePresence>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t0 || $[11] !== t4 || $[12] !== t6) {
    t7 = <>{t0}{t4}{t6}</>;
    $[10] = t0;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};
const ActivityPDP_Variants_ParticipantsInput: React.FC<{
  /** Number of participants */
  participants: {
    state: number;
    setState(participants: number): void;
    min: number | null;
    max: number | null;
  };
  activity: {
    priceType: NonNullable<ActivityFields['priceType']>;
    variants: Array<ActivityFields['activityVariants'][number] & {
      pricePerParticipant: number;
    }>;
  };
}> = props => {
  const i18n = useI18n();
  const vat = useVATRate();
  const priceRange = useMemo(() => variantPriceSpan(props.activity.variants.map(x => x.pricePerParticipant)), [props.activity.variants]);
  const upsellingCheapestPrice = useMemo(() => {
    if (!props.participants.state || !priceRange) return null;
    const roundedParticipants = Math.round(props.participants.state);
    const priceVatExcl = vat.removeFromEUR(priceRange.min);
    const maxCount = Math.max(roundedParticipants * 2, roundedParticipants + 1);
    for (let participants = roundedParticipants + 1; participants < maxCount; participants++) {
      const span = variantPriceSpan(props.activity.variants.map(variant => calculatePrice({
        variant,
        participants,
        priceType: props.activity.priceType
      }).pricePerParticipant)); // prettier-ignore

      if (span && span.min < priceVatExcl) {
        return {
          participants,
          minPrice: vat.calcEUR(span.min),
          maxPrice: vat.calcEUR(span.max)
        };
      }
    }
    return null;
  }, [props.participants.state, vat, priceRange, props.activity.variants, props.activity.priceType]);
  return <div className="mb-6 mr-auto flex flex-wrap gap-4 sm:mb-8">
            <ActivityPDP_ParticipantsInput initialValue={props.participants.state} onChange={value => props.participants.setState(value)} minParticipants={props.participants.min} maxParticipants={props.participants.max} className="w-auto" />

            <div className="mt-4">
                <p className="text-blue">
                    <I18n_AmountPerParticipant amount={Units.formatPriceRangeEUR(i18n, {
          minPrice: priceRange?.min,
          maxPrice: priceRange?.max
        })} disableEURPrefix />
                </p>
                {upsellingCheapestPrice && <p className="text-sm text-blue-700">
                        {i18n._('Cheaper from {count} participants: {range}', {
          count: upsellingCheapestPrice.participants,
          range: Units.formatPriceRangeEUR(i18n, {
            minPrice: upsellingCheapestPrice.minPrice,
            maxPrice: upsellingCheapestPrice.maxPrice
          })
        })}{' '}
                        <VATKindDisplay />
                    </p>}
            </div>
        </div>;
};
const ActivityPDP_ParticipantsInput: React.FC<Pick<React.ComponentProps<typeof NumberInput>, 'className'> & {
  initialValue: number;
  onChange(participants: number): void;
  minParticipants: number | null;
  maxParticipants: number | null;
}> = t0 => {
  const $ = _c(41);
  let initialValue;
  let maxParticipants;
  let minParticipants;
  let onChange;
  let props;
  if ($[0] !== t0) {
    ({
      initialValue,
      onChange,
      minParticipants,
      maxParticipants,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = initialValue;
    $[2] = maxParticipants;
    $[3] = minParticipants;
    $[4] = onChange;
    $[5] = props;
  } else {
    initialValue = $[1];
    maxParticipants = $[2];
    minParticipants = $[3];
    onChange = $[4];
    props = $[5];
  }
  const i18n = useI18n();
  const [participants, setParticipants] = useState(initialValue);
  let t1;
  if ($[6] !== maxParticipants || $[7] !== minParticipants || $[8] !== onChange || $[9] !== participants) {
    t1 = () => {
      if (participants === null || !std.Number.inRange(participants, minParticipants, maxParticipants)) {
        return;
      }
      startTransition(() => onChange(participants));
    };
    $[6] = maxParticipants;
    $[7] = minParticipants;
    $[8] = onChange;
    $[9] = participants;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== maxParticipants || $[12] !== minParticipants || $[13] !== participants) {
    t2 = [participants, minParticipants, maxParticipants];
    $[11] = maxParticipants;
    $[12] = minParticipants;
    $[13] = participants;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  useEffect(t1, t2);
  let validator;
  if ($[15] !== i18n || $[16] !== maxParticipants || $[17] !== minParticipants) {
    const MINIMUM_MESSAGE = i18n._("activity.error.minimumRequired", {
      minParticipants
    });
    validator = z.int(MINIMUM_MESSAGE).nonnegative(MINIMUM_MESSAGE);
    if (minParticipants !== null) {
      validator = validator.min(minParticipants, MINIMUM_MESSAGE);
    }
    if (maxParticipants !== null) {
      validator = validator.max(maxParticipants, i18n._("activity.error.maximumRequired", {
        maxParticipants
      }));
    }
    $[15] = i18n;
    $[16] = maxParticipants;
    $[17] = minParticipants;
    $[18] = validator;
  } else {
    validator = $[18];
  }
  const _validator = validator;
  let t3;
  if ($[19] !== _validator || $[20] !== participants) {
    t3 = _validator.safeParse(participants);
    $[19] = _validator;
    $[20] = participants;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  const validation = t3;
  let t4;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cx("relative rounded-4xl focus-within:z-1", "flex w-full items-center", "sm:max-w-[250px]");
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  const t5 = participants ?? undefined;
  let t6;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = value => setParticipants(value ?? null);
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== maxParticipants || $[25] !== minParticipants || $[26] !== participants) {
    t7 = () => {
      if (typeof participants !== "number") {
        return;
      }
      const clampedValue = std.Number.clamp(participants, minParticipants, maxParticipants);
      if (clampedValue === participants) {
        return;
      }
      setParticipants(clampedValue);
    };
    $[24] = maxParticipants;
    $[25] = minParticipants;
    $[26] = participants;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== i18n) {
    t8 = i18n._("participants");
    $[28] = i18n;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== props || $[31] !== t5 || $[32] !== t7 || $[33] !== t8) {
    t9 = <div className={t4}><NumberInput {...props} data-testid="activity-pdp-participants-input" value={t5} onChange={t6} onBlur={t7} min={0} allowUndefined={true} integerOnly={true} suffix={t8} /></div>;
    $[30] = props;
    $[31] = t5;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== validation.error || $[36] !== validation.success) {
    t10 = !validation.success && <span className="w-fit text-left text-xs text-red-600">{z.treeifyError(validation.error).errors.map(_temp)}</span>;
    $[35] = validation.error;
    $[36] = validation.success;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== t10 || $[39] !== t9) {
    t11 = <div className="relative flex flex-col gap-4">{t9}{t10}</div>;
    $[38] = t10;
    $[39] = t9;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  return t11;
};
const ActivityVariantCard: React.FC<{
  variant: ActivityFields['activityVariants'][number] & {
    pricePerParticipant: number;
  };
  onClick?: () => void;
}> = ({
  variant,
  onClick
}) => {
  const i18n = useI18n();
  const staffView = useStaffLayer_State();
  const shouldDisplayPrices = useShouldDisplayPrices();
  const portal = usePortalCtx();
  const vat = useVATRate();
  const isInBudget = useMemo(() => {
    if (portal.portal !== 'BIZZ') return true;
    return std.Number.inRange(variant.pricePerParticipant, maybeMap(portal.bizz.activityFilters.budget.min, vat.calcEUR) || undefined, maybeMap(portal.bizz.activityFilters.budget.max, vat.calcEUR) || undefined);
  }, [portal, variant.pricePerParticipant]);

  /** The tier ladder, sorted ascending and pre-formatted. `extraPrice` is the
   * flat opstartkosten of the tier and `price` the per-participant rate on
   * top of it — the same two columns the partner fills in in admin. */
  const priceRuleRows = useMemo(() => (variant.priceRules ?? []).filter(Filters.nonnull).slice().sort((a, b) => a.participants - b.participants).map(rule => ({
    participants: rule.participants,
    startupCost: Units.formatEUR(vat.calcEUR(rule.extraPrice ?? 0) * 100, i18n.locale),
    perParticipant: rule.price ? `${Units.formatEUR(vat.calcEUR(rule.price) * 100, i18n.locale)} ${i18n._('p.p.')}` : null
  })), [variant.priceRules, vat, i18n.locale]);

  /** Scale pricing steps the opstartkosten up at every tier boundary, so
   * per-person price can *rise* as the group grows (FUN-4983) — the ladder is
   * what makes an out-of-budget variant explicable rather than arbitrary.
   *
   * Only disabled variants expose the ladder. It shares the card's single
   * tooltip rather than getting its own: a nested tooltip inside a
   * `pointer-events`-dead, greyed-out element would never open. Composing both
   * messages keeps the rules reachable where they explain the disabled state. */
  const tooltipContent = useMemo(() => {
    const rules = variant.priceDescription?.trim() ?
    // The partner's own wording, already translated. Preferred over the
    // derived rows because it can explain what the numbers alone can't.
    <p className="whitespace-pre-line">{variant.priceDescription}</p> : priceRuleRows.length > 0 ? <div className="flex flex-col gap-0.5">
                {priceRuleRows.map(rule_0 => <div key={rule_0.participants} className="flex justify-between gap-6">
                        <span>
                            {rule_0.participants}
                            {'+ '}
                            {i18n._('participants')}
                        </span>
                        <span className="tabular-nums">
                            {rule_0.startupCost}
                            {rule_0.perParticipant ? ` + ${rule_0.perParticipant}` : ''}
                        </span>
                    </div>)}
            </div> : null;
    const showRules = shouldShowVariantPriceRules({
      isInBudget,
      shouldDisplayPrices,
      hasRules: !!rules
    });
    if (isInBudget && !showRules) return null;
    return <div className="flex max-w-xs flex-col gap-3 text-left">
                {!isInBudget && <p>{i18n._('portal.activity.variant.not-in-budget')}</p>}

                {showRules && <div className="flex flex-col gap-1">
                        <span className="text-xs font-semibold uppercase tracking-wide text-white/70">
                            {i18n._('Tiered pricing rules')}
                        </span>
                        {rules}
                    </div>}
            </div>;
  }, [isInBudget, shouldDisplayPrices, variant.priceDescription, priceRuleRows, i18n]);
  return <Tooltip enabled={!!tooltipContent} content={tooltipContent}>
            <span className={cx('group flex flex-col gap-4 rounded-3xl border-4 border-white bg-white p-8 transition-all', isInBudget ? ['opacity-100 grayscale-0', onClick && 'cursor-pointer hover:bg-blue-100'] : 'cursor-not-allowed opacity-50 grayscale')} onClick={isInBudget ? onClick : undefined}>
                <Title size="base" weight="bold" className="text-left">
                    {variant.name}
                </Title>

                <div className="flex w-full items-center justify-between">
                    <div className="flex flex-wrap items-center gap-4 text-blue-600">
                        {variant.duration && <div className="flex items-center gap-2">
                                <Icon name="time" size="sm" />
                                {variant.duration} {i18n._('hours')}
                            </div>}

                        {shouldDisplayPrices && <div className="flex items-center gap-2">
                                <Icon name="people-users" size="sm" />
                                <I18n_AmountPerParticipant amount={Units.formatEUR(variant.pricePerParticipant * 100, i18n.locale)} disableEURPrefix />
                            </div>}
                    </div>
                </div>

                {variant.description?.trim() &&
      // `div`, not `p`: the description is partner-authored rich text and
      // routinely contains block tags. A `<p>` inside a `<p>` makes the
      // browser parser auto-close the outer one, so the DOM no longer
      // matches what React rendered -> hydration mismatch.
      <div className="text-left text-blue-700" dangerouslySetInnerHTML={{
        __html: variant.description
      }} />}

                {staffView.shouldShow && variant.adminNotes?.trim() && <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 text-left">
                        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600">
                            {i18n._('pdp.staff.variant_internal_notes')}
                        </p>
                        <p className="whitespace-pre-wrap text-sm text-blue-800">
                            {variant.adminNotes}
                        </p>
                    </div>}

                {isInBudget && onClick && <Button variant="outline-to-secondary" iconBefore="plus" iconClassName="text-secondary group-hover:text-white" className="mr-auto text-blue" size="sm">
                        {i18n._('Add to quotation')}
                    </Button>}
            </span>
        </Tooltip>;
};
function _temp(message, _i, arr) {
  if (arr.length === 1) {
    return message;
  }
  return <>- {message}<br /></>;
}